import { useState, useCallback } from 'react';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from 'constants/apiConfig';
import { docxExport } from '../../DisasterGPT/DisasterGPTDataHelpers/DGPTExport';
import type { SitrepSectionForExport } from '../../DisasterGPT/hooks/useSitrepPdfExport';
import { useSitrepPdfExport } from '../../DisasterGPT/hooks/useSitrepPdfExport';
import { GetSitrepsResponse } from 'components/Reports/reports.types';
import { PollSitrepResponse } from '../../DisasterGPT/hooks/useSitrepsV2';

/** Hook for fetching full sitrep data with sections from the API */
function useFetchFullSitrepData() {
  const fetchFullSitrepData = useCallback(
    async (sitrep: GetSitrepsResponse): Promise<PollSitrepResponse | null> => {
      try {
        const response = await axios.get<PollSitrepResponse>(
          `${config.apiGateway.sitrepsV2}/pollSitrep`,
          { params: { sitrep_id: sitrep.id } }
        );
        return response.data;
      } catch (err) {
        console.warn(`Failed to fetch sitrep ${sitrep.id}:`, err);
        return null;
      }
    },
    []
  );

  return { fetchFullSitrepData };
}

interface UseExportAllSitrepsReturn {
  exportAllSitreps: (
    sitreps: GetSitrepsResponse[],
    filterName: string | null,
    format: 'pdf' | 'docx'
  ) => Promise<void>;
  isExporting: boolean;
}

/** Hook for exporting multiple sitreps as a single PDF or DOCX document */
export function useExportAllSitreps(): UseExportAllSitrepsReturn {
  const [isExporting, setIsExporting] = useState(false);
  const { exportPdf } = useSitrepPdfExport();
  const { fetchFullSitrepData } = useFetchFullSitrepData();

  const exportAllSitreps = useCallback(
    async (
      sitreps: GetSitrepsResponse[],
      filterName: string | null,
      format: 'pdf' | 'docx'
    ): Promise<void> => {
      if (sitreps.length === 0) {
        toast.warning('No reports to export');
        return;
      }

      setIsExporting(true);
      try {
        const timestamp = dayjs().format('YYYY-MM-DD hh-mm A');
        const baseName = `Reports_${filterName || 'All'}_${timestamp}`;

        // Combine all sitreps into one document
        // Each sitrep becomes a header followed by its sections
        const allSections: SitrepSectionForExport[] = [];

        // Fetch all sitreps' full data with sections in parallel for better performance
        const fullSitreps = await Promise.all(
          sitreps.map((sitrep) => fetchFullSitrepData(sitrep))
        );

        // Process each sitrep and its fetched data
        for (let i = 0; i < sitreps.length; i++) {
          const sitrep = sitreps[i];
          const fullSitrep = fullSitreps[i];

          // Add sitrep name as a header (mark as report boundary for horizontal line)
          allSections.push({
            name: sitrep.name || 'Untitled Report',
            content: sitrep.description || '',
            timestamp: sitrep.timestamp
              ? new Date(sitrep.timestamp).toISOString()
              : '',
            isReportBoundary: true,
          });

          // Add each section's content (without timestamps)
          if (fullSitrep?.sections?.length) {
            for (const section of fullSitrep.sections) {
              allSections.push({
                name: section.name,
                content:
                  section.section_versions?.[0]?.sitrep_section_text || '',
                // No timestamp for individual sections - only report boundaries have timestamps
              });
            }
          }
        }

        if (format === 'pdf') {
          const blob = await exportPdf(
            allSections,
            `All Reports - ${filterName || 'All'}`
          );
          saveAs(blob, `${baseName}.pdf`);
          toast.success('PDF downloaded');
        } else {
          const data = allSections.map((section) => ({
            role: section.name,
            content: section.content,
            timestamp: section.timestamp,
            isReportBoundary: section.isReportBoundary,
          }));
          docxExport(data, 'Download', baseName);
          toast.success('Word document downloaded');
        }
      } catch (error) {
        console.error('Failed to export all:', error);
        toast.error(`Failed to export as ${format.toUpperCase()}`);
        throw error;
      } finally {
        setIsExporting(false);
      }
    },
    [exportPdf, fetchFullSitrepData]
  );

  return { exportAllSitreps, isExporting };
}
